import type { AggregatedProgress, AgeGroup, LocalDayRecord } from "./types";

export interface InstallationState {
  shareToken: string;
  installationId: string;
  credential: string;
  seed: string;
  ageGroup: AgeGroup;
  preferredTime: string;
  timezone: string;
  activatedAt: string;
}

export interface DigStorage {
  getInstallation(): Promise<InstallationState | null>;
  setInstallation(state: InstallationState): Promise<void>;
  getDays(): Promise<LocalDayRecord[]>;
  upsertDay(day: LocalDayRecord): Promise<void>;
  getLastSyncProgress(): Promise<AggregatedProgress | null>;
  setLastSyncProgress(progress: AggregatedProgress): Promise<void>;
}

export function createMemoryStorage(): DigStorage {
  let installation: InstallationState | null = null;
  let days: LocalDayRecord[] = [];
  let lastSync: AggregatedProgress | null = null;
  return {
    async getInstallation() {
      return installation;
    },
    async setInstallation(state) {
      installation = state;
    },
    async getDays() {
      return days;
    },
    async upsertDay(day) {
      const i = days.findIndex((d) => d.localDate === day.localDate);
      if (i >= 0) days[i] = day;
      else days.push(day);
    },
    async getLastSyncProgress() {
      return lastSync;
    },
    async setLastSyncProgress(progress) {
      lastSync = progress;
    },
  };
}

const INSTALL_KEY = "jari-dig-installation";
const DAYS_KEY = "jari-dig-days";
const SYNC_KEY = "jari-dig-last-sync";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Browser/dev storage; native app uses SQLite */
export function createLocalDigStorage(): DigStorage {
  return {
    async getInstallation() {
      return readJson<InstallationState | null>(INSTALL_KEY, null);
    },
    async setInstallation(state) {
      writeJson(INSTALL_KEY, state);
    },
    async getDays() {
      return readJson<LocalDayRecord[]>(DAYS_KEY, []);
    },
    async upsertDay(day) {
      const days = readJson<LocalDayRecord[]>(DAYS_KEY, []);
      const i = days.findIndex((d) => d.localDate === day.localDate);
      if (i >= 0) days[i] = day;
      else days.push(day);
      writeJson(DAYS_KEY, days);
    },
    async getLastSyncProgress() {
      return readJson<AggregatedProgress | null>(SYNC_KEY, null);
    },
    async setLastSyncProgress(progress) {
      writeJson(SYNC_KEY, progress);
    },
  };
}
